import { QUESTION_NAMES } from 'client-portal/utils/prescreener/constants';
import { TrackedMap } from 'tracked-built-ins';
import { encodeFileToBase64 } from 'client-portal/utils/prescreener/encode-file-to-base64';
import Service from '@ember/service';

const getFileComparator =
  (questionName, type, side = null) =>
  ({ metadata }) =>
    metadata.questionName === questionName &&
    metadata.type === type &&
    (!side || metadata.side === side);

export default class PrescreenerUploadService extends Service {
  files = new TrackedMap();

  get insuranceCardFront() {
    let questionName = QUESTION_NAMES.INSURANCE_CARD_UPLOAD;
    if (!this.files.get(questionName)) return null;

    let fileWithMetadata = this.files
      .get(questionName)
      .find(getFileComparator(questionName, 'client', 'front'));
    return fileWithMetadata?.uploadFile;
  }

  get insuranceCardBack() {
    let questionName = QUESTION_NAMES.INSURANCE_CARD_UPLOAD;
    if (!this.files.get(questionName)) return null;

    let fileWithMetadata = this.files
      .get(questionName)
      .find(getFileComparator(questionName, 'client', 'back'));
    return fileWithMetadata?.uploadFile;
  }

  get filesToUpload() {
    if (!this.files.size) return [];

    return Array.from(this.files.values())
      .flat()
      .map(({ uploadFile, metadata }) => ({
        file: uploadFile?.file,
        metadata,
      }));
  }

  set insuranceCardFront(uploadFile) {
    this.addFile(uploadFile, {
      'question_name': QUESTION_NAMES.INSURANCE_CARD_UPLOAD,
      type: 'client',
      side: 'front',
    });
  }

  set insuranceCardBack(uploadFile) {
    this.addFile(uploadFile, {
      'question_name': QUESTION_NAMES.INSURANCE_CARD_UPLOAD,
      type: 'client',
      side: 'back',
    });
  }

  addFile(uploadFile, metadata) {
    const { questionName, type, side } = metadata;
    const fileComparator = getFileComparator(questionName, type, side);
    const existingFiles = this.files.get(questionName) ?? [];

    const updatedFiles = uploadFile
      ? this.#addOrUpdateFile({ existingFiles, uploadFile, metadata, fileComparator })
      : this.#removeFile(existingFiles, fileComparator);

    this.files.set(questionName, updatedFiles);
  }

  async getBase64EncodedFiles() {
    let fileGroups = Array.from(this.files.values()).flat();

    let encoded = await Promise.all(
      fileGroups.map(async ({ uploadFile, metadata }) => {
        if (!uploadFile?.file) return null;

        return await encodeFileToBase64(uploadFile.file, metadata);
      })
    );

    return encoded.filter(Boolean);
  }

  async getEncodedAnswers(answerModel) {
    let file = await this.getBase64EncodedFiles();
    answerModel.set('files', file);
    return answerModel;
  }

  #addOrUpdateFile({ existingFiles, uploadFile, metadata, fileComparator }) {
    const existingFile = existingFiles.find(fileComparator);

    if (existingFile) {
      existingFile.uploadFile = uploadFile;
      return existingFiles;
    }

    const newFile = {
      metadata,
      uploadFile,
    };

    return existingFiles.length ? [...existingFiles, newFile] : [newFile];
  }

  #removeFile(existingFiles, fileComparator) {
    return existingFiles.filter(fileWithMetadata => !fileComparator(fileWithMetadata));
  }
}
